import { Trans, useLingui } from "@lingui/react/macro";

import { share } from "../../model/change";
import type { DashboardCohort } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";
import { dayDate } from "./dayDate";

type CohortsProps = {
  cohorts: DashboardCohort[];
};

// «Возврат по дням прихода»: для каждого дня с новыми игроками — доля тех, кто снова заходил
// на 1-й, 3-й и 7-й день после входа или позже. Ячейка темнее — доля больше; день ещё не
// наступил — «—».
export const Cohorts = ({ cohorts }: CohortsProps) => {
  const { i18n, t } = useLingui();
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const dayFormat = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    // eslint-disable-next-line lingui/no-unlocalized-strings -- часовой пояс, а не текст
    timeZone: "UTC",
  });
  const cell = (returned: number | null, joined: number) => {
    const value = returned === null ? null : share(returned, joined);
    return (
      <td
        className="px-2 py-1.5 text-right font-mono tabular-nums data-[empty]:text-subtle"
        data-empty={value === null ? "" : undefined}
        title={returned === null ? undefined : t`${returned} of ${joined}`}
        style={
          value === null
            ? undefined
            : {
                backgroundColor: `color-mix(in oklab, var(--color-chart-amber) ${String(
                  Math.round(10 + value * 60),
                )}%, transparent)`,
              }
        }
      >
        {value === null ? "—" : percent.format(value)}
      </td>
    );
  };

  return (
    <DashboardCard
      title={<Trans>Return by sign-up day</Trans>}
      description={
        <Trans>
          Share of players who joined that day and came back on day N after signing in or later
        </Trans>
      }
    >
      {cohorts.length === 0 ? (
        <p className="text-[13px] text-muted">
          <Trans>No new players in this period.</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[380px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-xs text-subtle">
                <th scope="col" className="pt-1 pr-2 pb-2 font-semibold">
                  <Trans>Joined</Trans>
                </th>
                <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                  <Trans>Players</Trans>
                </th>
                <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                  <Trans>Next day</Trans>
                </th>
                <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                  <Trans>After 3 days</Trans>
                </th>
                <th scope="col" className="pt-1 pb-2 pl-2 text-right font-semibold">
                  <Trans>After a week</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort) => (
                <tr key={cohort.day} data-cohort={cohort.day} className="border-t border-border/60">
                  <th
                    scope="row"
                    className="py-1.5 pr-2 text-left font-medium whitespace-nowrap text-foreground-soft"
                  >
                    {dayFormat.format(dayDate(cohort.day))}
                  </th>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{cohort.joined}</td>
                  {cell(cohort.returned_day1, cohort.joined)}
                  {cell(cohort.returned_day3, cohort.joined)}
                  {cell(cohort.returned_day7, cohort.joined)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-subtle">
        <Trans>“—” — too early for this day: N days since sign-in have not passed yet.</Trans>
      </p>
    </DashboardCard>
  );
};
